import { DOCUMENT_FIELDS } from "../assets/instateamMasterConfig";
import {
  AppIcon,
  InfoBlock,
  LabeledInput,
} from "../components/InstateamMasterComponents";
import {
  buildEmployeeAddress,
  currencyFormatter,
  formatWorkingHoursLabel,
  getInitials,
  toAbsoluteUrl,
} from "../utils/adminHelpers";
import AddEmployeePage from "./AddEmployeePage";

const DOCUMENT_LABELS = DOCUMENT_FIELDS.reduce((accumulator, field) => {
  accumulator[field.key] = field.label;
  return accumulator;
}, {});

export default function EmployeeDetailsPage({
  employee,
  isLoadingEmployee,
  isEditingEmployee,
  employeeForm,
  employeeFiles,
  onEmployeeFieldChange,
  onEmployeeFileChange,
  onEmployeeSubmit,
  isSavingEmployee,
  onCancelEdit,
  onBack,
  onBeginEdit,
  onToggleStatus,
  isUpdatingEmployeeStatus,
  passwordForm,
  onPasswordFieldChange,
  onPasswordSubmit,
  isSavingPassword,
  onJumpToPassword,
  departmentOptions = [],
  designationOptions = [],
}) {
  if (isLoadingEmployee && !employee) {
    return <section className="table-card detail-empty-card">Loading employee details...</section>;
  }

  if (!employee) {
    return (
      <section className="table-card detail-empty-card">
        <h3>Employee not found</h3>
        <p>This employee record could not be loaded right now.</p>
        <button className="secondary-button" type="button" onClick={onBack}>
          Back to Employees
        </button>
      </section>
    );
  }

  const documentEntries = Object.entries(employee.documents || {}).filter(
    ([, file]) => file?.url
  );

  return (
    <section className="employee-detail-page">
      <div className="detail-topbar">
        <button className="secondary-button detail-back-button" type="button" onClick={onBack}>
          <AppIcon name="chevronLeft" />
          Back to Employees
        </button>

        <div className="detail-action-group">
          <button
            className="secondary-button"
            type="button"
            onClick={isEditingEmployee ? onCancelEdit : onBeginEdit}
          >
            <AppIcon name="edit" />
            {isEditingEmployee ? "Cancel Edit" : "Edit Details"}
          </button>

          <button className="secondary-button" type="button" onClick={onJumpToPassword}>
            <AppIcon name="lock" />
            Change Password
          </button>

          <button
            className={`status-action-button ${employee.status === "active" ? "danger" : "success"}`}
            type="button"
            onClick={onToggleStatus}
            disabled={isUpdatingEmployeeStatus}
          >
            {isUpdatingEmployeeStatus
              ? "Updating..."
              : employee.status === "active"
                ? "Disable Employee"
                : "Enable Employee"}
          </button>
        </div>
      </div>

      <div className="detail-hero-card">
        <div className="detail-hero-main">
          {employee.profilePhoto?.url ? (
            <img
              src={toAbsoluteUrl(employee.profilePhoto.url)}
              alt={employee.fullName}
              className="detail-hero-avatar"
            />
          ) : (
            <div className="avatar-circle detail-hero-avatar">{getInitials(employee.fullName)}</div>
          )}

          <div className="detail-hero-copy">
            <div className="detail-hero-label">Employee Profile</div>
            <h3>{employee.fullName}</h3>
            <p>
              {employee.designation} • {employee.department}
            </p>

            <div className="detail-hero-meta">
              <span>{employee.employeeId}</span>
              <span>@{employee.username}</span>
              <span>{employee.email}</span>
            </div>
          </div>
        </div>

        <div className="detail-hero-side">
          <span className={`status-badge ${employee.status === "active" ? "active" : "inactive"}`}>
            {employee.status === "active" ? "Active" : "Inactive"}
          </span>
          <strong>{currencyFormatter.format(Number(employee.basicSalary || 0))}</strong>
          <small>{employee.payFrequency || "Pay frequency not set"}</small>
        </div>
      </div>

      {isEditingEmployee ? (
        <div className="detail-edit-shell">
          <AddEmployeePage
            mode="edit"
            employeeForm={employeeForm}
            employeeFiles={employeeFiles}
            onFieldChange={onEmployeeFieldChange}
            onFileChange={onEmployeeFileChange}
            onCancel={onCancelEdit}
            onSubmit={onEmployeeSubmit}
            isSavingEmployee={isSavingEmployee}
            employeeIdValue={employee.employeeId}
            showPasswordFields={false}
            existingProfilePhoto={employee.profilePhoto}
            existingDocuments={employee.documents || {}}
            submitLabel="Save Changes"
            departmentOptions={departmentOptions}
            designationOptions={designationOptions}
          />
        </div>
      ) : (
        <>
          <div className="detail-grid">
            <section className="form-section-card detail-section-card">
              <div className="section-heading">
                <h3>Personal Information</h3>
              </div>

              <div className="detail-info-grid">
                <InfoBlock label="Username" value={employee.username} />
                <InfoBlock label="Email" value={employee.email} />
                <InfoBlock label="Phone Number" value={employee.phoneNumber} />
                <InfoBlock label="Date of Birth" value={employee.dateOfBirth} />
                <InfoBlock label="Gender" value={employee.gender} />
                <InfoBlock label="Marital Status" value={employee.maritalStatus || "—"} />
              </div>
            </section>

            <section className="form-section-card detail-section-card">
              <div className="section-heading">
                <h3>Employment Information</h3>
              </div>

              <div className="detail-info-grid">
                <InfoBlock label="Employee ID" value={employee.employeeId} />
                <InfoBlock label="Department" value={employee.department} />
                <InfoBlock label="Designation" value={employee.designation} />
                <InfoBlock label="Role" value={employee.role} />
                <InfoBlock label="Date of Joining" value={employee.dateOfJoining} />
                <InfoBlock label="Employment Type" value={employee.employmentType} />
                <InfoBlock
                  label="Working Hours"
                  value={
                    employee.workingHoursLabel ||
                    formatWorkingHoursLabel(employee.workShiftStart, employee.workShiftEnd)
                  }
                />
                <InfoBlock label="Reporting Manager" value={employee.reportingManager || "—"} />
                <InfoBlock label="Status" value={employee.status} />
              </div>
            </section>
          </div>

          <div className="detail-grid">
            <section className="form-section-card detail-section-card">
              <div className="section-heading">
                <h3>Salary & Banking</h3>
              </div>

              <div className="detail-info-grid">
                <InfoBlock
                  label="Basic Salary"
                  value={currencyFormatter.format(Number(employee.basicSalary || 0))}
                />
                <InfoBlock
                  label="HRA"
                  value={currencyFormatter.format(Number(employee.houseRentAllowance || 0))}
                />
                <InfoBlock
                  label="Conveyance"
                  value={currencyFormatter.format(Number(employee.conveyanceAllowance || 0))}
                />
                <InfoBlock
                  label="Special Allowance"
                  value={currencyFormatter.format(Number(employee.specialAllowance || 0))}
                />
                <InfoBlock
                  label="Gross Salary"
                  value={currencyFormatter.format(Number(employee.grossSalary || 0))}
                />
                <InfoBlock
                  label="Total Deductions"
                  value={currencyFormatter.format(Number(employee.totalDeductions || 0))}
                />
                <InfoBlock
                  label="Net Salary"
                  value={currencyFormatter.format(Number(employee.netSalary || 0))}
                />
                <InfoBlock
                  label="Annual CTC"
                  value={currencyFormatter.format(Number(employee.ctcAnnual || 0))}
                />
                <InfoBlock label="Pay Frequency" value={employee.payFrequency} />
                <InfoBlock label="Tax Regime" value={employee.taxRegime || "—"} />
                <InfoBlock label="UAN Number" value={employee.uanNumber || "—"} />
                <InfoBlock label="PF Number" value={employee.pfNumber || "—"} />
                <InfoBlock label="Bank Name" value={employee.bankName} />
                <InfoBlock label="Account Number" value={employee.accountNumber} />
                <InfoBlock label="IFSC Code" value={employee.ifscCode} />
                <InfoBlock label="PAN Number" value={employee.panNumber || "—"} />
              </div>
            </section>

            <section className="form-section-card detail-section-card">
              <div className="section-heading">
                <h3>Address</h3>
              </div>

              <div className="detail-info-grid">
                <InfoBlock label="Address" value={buildEmployeeAddress(employee) || "—"} />
                <InfoBlock label="Country" value={employee.country} />
              </div>
            </section>
          </div>
        </>
      )}

      <section className="form-section-card detail-section-card">
        <div className="section-heading">
          <h3>Employee Documents</h3>
        </div>

        {documentEntries.length ? (
          <div className="employee-document-card-grid">
            {documentEntries.map(([key, file]) => (
              <a
                key={key}
                className="employee-document-card"
                href={toAbsoluteUrl(file.url)}
                target="_blank"
                rel="noreferrer"
              >
                <div className="employee-document-card-icon">
                  <AppIcon name="documents" />
                </div>
                <div>
                  <strong>{DOCUMENT_LABELS[key] || key}</strong>
                  <span>{file.originalName || "Stored document"}</span>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <p className="documents-empty">No documents uploaded yet.</p>
        )}
      </section>

      <section id="employee-password-card" className="form-section-card detail-section-card">
        <div className="section-heading">
          <h3>Change Login Password</h3>
        </div>

        <form className="password-form-grid" onSubmit={onPasswordSubmit}>
          <LabeledInput
            label="New Password"
            required
            type="password"
            value={passwordForm.newPassword}
            onChange={(value) =>
              onPasswordFieldChange((current) => ({ ...current, newPassword: value }))
            }
            placeholder="Enter new password"
          />

          <LabeledInput
            label="Confirm Password"
            required
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(value) =>
              onPasswordFieldChange((current) => ({ ...current, confirmPassword: value }))
            }
            placeholder="Re-enter new password"
          />

          <div className="password-form-actions">
            <button className="primary-button" type="submit">
              {isSavingPassword ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </section>
    </section>
  );
}
